import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Input, Empty, Spin, Typography, Button, Space, message, Tag } from 'antd';
import { TagsOutlined, PlusOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { getAvailablePhraseGroups, type PhraseGroupView } from '@/api/phraseLists';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';

const { Text } = Typography;

// Right-anchored drawer that lists every phrase bucket in scope (global +
// merchant) and lets the user one-click "Attach" each to the current agent.
// Already-attached buckets are hidden. Mirrors LinkKnowledgeDrawer (drawers >
// modals for picker flows) so the agent detail page stays visible behind.

interface LinkPhraseBucketsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Set of PhraseTermGroup ids already attached (hidden from the picker). */
  alreadyLinkedIds: Set<number>;
  agentDisplayName: string;
}

export const LinkPhraseBucketsDrawer = observer(function LinkPhraseBucketsDrawer({
  open,
  onClose,
  alreadyLinkedIds,
  agentDisplayName,
}: LinkPhraseBucketsDrawerProps) {
  const { agentDetailStore: detail, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [allGroups, setAllGroups] = useState<PhraseGroupView[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [linkingId, setLinkingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getAvailablePhraseGroups()
      .then((res) => {
        if (cancelled) return;
        setAllGroups(res);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load phrase buckets');
        setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  const candidates = allGroups.filter((g) => {
    if (alreadyLinkedIds.has(g.id)) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (g.title ?? '').toLowerCase().includes(q) || (g.description ?? '').toLowerCase().includes(q);
  });

  async function handleAttach(g: PhraseGroupView) {
    setLinkingId(g.id);
    try {
      await detail.attachPhraseGroup(g.id);
      message.success(`Attached "${g.title}" to ${agentDisplayName}`);
      trackEvent('AgentPhraseList.Attached', { agentId: detail.agentId ?? -1, groupId: g.id });
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to attach phrase bucket');
    } finally {
      setLinkingId(null);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="40%"
      destroyOnClose
      title={
        <Space size={10} align="center">
          <TagsOutlined style={{ color: colors.ink[600], fontSize: 18 }} />
          <span>Attach phrase buckets to this agent</span>
        </Space>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Pick a phrase bucket to bias <b>{agentDisplayName}</b>'s voice transcription toward its vocabulary
          (drug names, regimens, symptoms). Takes effect when the <b>Phrase list</b> toggle is on.
        </Text>

        <Input.Search
          placeholder="Search buckets by title or description…"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {error && (
          <div
            style={{
              padding: 12,
              background: isDark ? `${colors.red[700]}30` : colors.red[100],
              border: `1px solid ${colors.red[500]}`,
              borderRadius: 8,
              fontSize: 12.5,
              color: isDark ? colors.red[100] : colors.red[700],
            }}
          >
            {error}
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {isLoading ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Spin />
            </div>
          ) : candidates.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  search.trim()
                    ? 'No buckets match your search.'
                    : alreadyLinkedIds.size > 0
                    ? 'Every available bucket is already attached to this agent.'
                    : 'No phrase buckets exist yet. Create one from the Phrase Lists page first.'
                }
              />
            </div>
          ) : (
            candidates.map((g) => (
              <PhraseBucketCard
                key={g.id}
                group={g}
                isDark={isDark}
                isLinking={linkingId === g.id}
                onAttach={() => void handleAttach(g)}
              />
            ))
          )}
        </div>
      </div>
    </Drawer>
  );
});

// ── Bucket row card — title + description + term count + Attach button ──────

const PhraseBucketCard = function PhraseBucketCard({
  group,
  isDark,
  isLinking,
  onAttach,
}: {
  group: PhraseGroupView;
  isDark: boolean;
  isLinking: boolean;
  onAttach: () => void;
}) {
  return (
    <div
      style={{
        padding: '12px 14px',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        background: isDark ? colors.slate[800] : '#FFFFFF',
        borderRadius: 8,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: isDark ? colors.slate[50] : colors.slate[900], marginBottom: 2 }}>
          {group.title}
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 400, marginLeft: 8 }}>
            #{group.id}
          </Text>
          {group.merchantId == null && <Tag style={{ fontSize: 10, marginLeft: 8 }}>Global</Tag>}
        </div>
        {group.description && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            {group.description}
          </Text>
        )}
        <Space size={6} wrap>
          <Tag icon={<TagsOutlined />} style={{ fontSize: 11 }}>
            {group.termCount} term{group.termCount === 1 ? '' : 's'}
          </Tag>
          {!group.isActive && <Tag style={{ fontSize: 11 }}>Inactive</Tag>}
        </Space>
      </div>
      <Button type="primary" ghost size="small" icon={<PlusOutlined />} loading={isLinking} onClick={onAttach}>
        Attach
      </Button>
    </div>
  );
};
